import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';
import { ProductType } from './ticket.constants';

export class TicketFlowService extends InjectableClass {
  constructor(
    TicketRoutesReceiving,
    TicketRoutesReceivingDirect,
    TicketRoutesReceivingLight,
    TicketRoutesTransferring,
    TicketRoutesFrontOffice,
    TicketRoutesFrontOfficeWithStandingOrders,
    TicketRoutesTransferringFrontOffice,
    TicketRoutesCustomer,
    TicketRoutesDWS,
    TicketRoutesDataCapturingProvider,
    TicketType,
    TicketTypeMatching,
    TicketStates,
    TicketStatesTransferring,
    TicketStatesReceiving,
    ReceivingKwhTypes,
    receivingCreateRequestStateName,
    uploadWarrantStateName,
    userService,
    envService,
    featureFlagService,
  ) {
    'ngInject';
    super({
      TicketRoutesReceiving,
      TicketRoutesReceivingDirect,
      TicketRoutesReceivingLight,
      TicketRoutesTransferring,
      TicketRoutesFrontOffice,
      TicketRoutesFrontOfficeWithStandingOrders,
      TicketRoutesTransferringFrontOffice,
      TicketRoutesCustomer,
      TicketRoutesDWS,
      TicketRoutesDataCapturingProvider,
      TicketType,
      TicketTypeMatching,
      TicketStates,
      TicketStatesTransferring,
      TicketStatesReceiving,
      ReceivingKwhTypes,
      receivingCreateRequestStateName,
      uploadWarrantStateName,
      userService,
      envService,
      featureFlagService,
    });
  }

  get transferringFlow() {
    return this.TicketType.Transferring;
  }

  get receivingFlow() {
    return this.TicketType.Receiving;
  }

  getStatesForType(ticketType) {
    if (ticketType === this.TicketType.Transferring) {
      return this.TicketStatesTransferring;
    }
    if (ticketType === this.TicketType.Receiving) {
      return this.TicketStatesReceiving;
    }
    return this.TicketStates;
  }

  getCreateRoute(ticketType) {
    if (ticketType === this.TicketType.Transferring) {
      return this.uploadWarrantStateName;
    }
    return this.receivingCreateRequestStateName;
  }

  customerConfirms(ticket) {
    return ticket && ticket.guidanceType === this.ReceivingKwhTypes.ONLINE;
  }

  getTypeForTicket(ticket) {
    if (ticket) {
      return _.findKey(this.TicketTypeMatching, (value, key) =>
        _.includes(value, ticket.requestType),
      );
    }
    return null;
  }

  isTransferringTicket(ticket) {
    const ticketType = this.getTypeForTicket(ticket);
    return ticketType === this.TicketType.Transferring;
  }

  isReceivingTicket(ticket) {
    const ticketType = this.getTypeForTicket(ticket);
    return ticketType === this.TicketType.Receiving;
  }

  canHandleTicket(ticket, route, routeConditions) {
    // Check routes readonly object is some props match the conditions
    // If they do, user cannot handle ticket
    const routeHandle =
      !route ||
      !route.readonly ||
      _.every(route.readonly, (val, key) => {
        if (!routeConditions || !routeConditions[key]) {
          return true;
        }
        if (angular.isFunction(val)) {
          return !val(routeConditions[key]);
        }
        return routeConditions[key] !== val;
      });

    const ticketType = this.getTypeForTicket(ticket);
    return this.userService.canHandle(ticketType) && routeHandle;
  }

  getRoutesForTicket(ticket, product = null) {
    if (product === ProductType.DWS) {
      return this.TicketRoutesDWS;
    }

    // Routes for Customer
    if (this.userService.isCustomerEntryUser()) {
      return this.TicketRoutesCustomer;
    }

    // Routes for FrontOffice

    if (this.userService.isFrontOfficeWithStandingOrders()) {
      return this.TicketRoutesFrontOfficeWithStandingOrders;
    }

    if (
      this.userService.isFrontOffice() &&
      !this.userService.isFrontOfficeWithComplete()
    ) {
      if (this.isTransferringTicket(ticket)) {
        return this.TicketRoutesTransferringFrontOffice;
      }
      return this.TicketRoutesFrontOffice;
    }

    // Routes for BackOfficeDirect Receiving
    if (
      this.userService.isBackOfficeDirect() &&
      this.isReceivingTicket(ticket)
    ) {
      if (
        this.featureFlagService.isFeatureActive(
          this.featureFlagService.FeatureNames.FF_DISABLE_VERIFYING_FINANCES,
        )
      ) {
        return this.TicketRoutesReceivingLight;
      }
      return this.TicketRoutesReceivingDirect;
    }

    // Routes for DataCapturingProvider Receiving
    if (this.userService.isDataCapturingProvider()) {
      return this.TicketRoutesDataCapturingProvider;
    }

    // Routes for Transferring Flow
    if (this.isTransferringTicket(ticket)) {
      return this.TicketRoutesTransferring;
    }

    // Routes for Receiving Flow
    return this.TicketRoutesReceiving;
  }

  getRouteForTicket(ticket, route) {
    const ticketType = this.getTypeForTicket(ticket);
    if (ticketType) {
      return `app.${ticketType}.${route}`;
    }
    return 'app.home';
  }

  getRouteConditions(ticket) {
    // Gather all possible route conditions
    const { userService, envService } = this;
    const conditions = {};

    if (ticket.formValues) {
      conditions.closeAccount = !!ticket.formValues.closeAccount;
    }

    conditions.customerConfirms = this.customerConfirms(ticket);
    conditions.isCobaAndNotFom =
      !userService.hasRole(userService.Roles.FrontOffice) &&
      envService.isCustom(envService.custom.commerzbank);

    const forcedShipment = _.find(ticket.history, (h) => {
      return (
        h.eventName ===
        this.TicketStates.FINANCES_COMPLETED_WITH_PAYMENT_PARTNERS
      );
    });
    if (forcedShipment) {
      conditions.forcedShipment = forcedShipment.timestamp;
    }

    return conditions;
  }
}
